"use client";

import { useState, useEffect } from "react";
import { useScrollReveal } from "@/hooks/common/useScrollReveal";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { googleLogo, trustpilotLogo } from "@/lib/gcsImagePaths";
import { Review } from "@/types/review";
import GcsImage from "./GcsImage";
import { reviewPlatforms } from "@/lib/reviewsData";
import type { AllRatingsData } from "@/lib/fetchRatings";

interface ReviewsSectionProps {
  title: string;
  showSectionHeading?: boolean;
  reviewSets: Review[][];
  autoScrollInterval?: number;
  // Style variants
  cardBorderClassName?: string;
  cardContentPadding?: string;
  cardAnimationClassName?: string;
  sectionTransitionClassName?: string;
  sectionVisibleClassName?: string;
  useVisibilityStyle?: boolean;
  ratings?: AllRatingsData;
  sectionSpacingClassName?: string;
  sectionBackgroundClassName?: string;
}

function roundToHalf(rating: number): number {
  return Math.round(rating * 2) / 2;
}

function toDisplayRating(value: unknown): number | null {
  const parsed =
    typeof value === "number" ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.min(5, parsed));
}

function getStarFillPercent(rating: number, index: number): number {
  const normalizedRating = roundToHalf(Math.max(0, Math.min(5, rating)));
  const starValue = normalizedRating - index;

  if (starValue >= 1) return 100;
  if (starValue >= 0.5) return 50;
  return 0;
}

function RatingStars({
  rating,
  filledClassName,
}: {
  rating: number;
  filledClassName: string;
}) {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => {
        const fillPercent = getStarFillPercent(rating, i);

        return (
          <span key={i} className="relative inline-flex h-5 w-5">
            <Star className="absolute inset-0 h-5 w-5 fill-gray-200 text-gray-200" />
            {fillPercent > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${fillPercent}%` }}
              >
                <Star className={`h-5 w-5 ${filledClassName}`} />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

export default function ReviewsSection({
  title,
  showSectionHeading = true,
  reviewSets,
  autoScrollInterval = 5000,
  cardBorderClassName = "border-2 border-border",
  cardContentPadding = "pt-6 pb-6 px-4",
  cardAnimationClassName = "",
  sectionTransitionClassName = "transition-opacity duration-700 will-change-[opacity]",
  sectionVisibleClassName = "opacity-100",
  useVisibilityStyle = false,
  ratings,
  sectionSpacingClassName = "pt-16 md:pt-24 pb-8",
  sectionBackgroundClassName = "bg-background",
}: ReviewsSectionProps) {
  const [currentReviewSet, setCurrentReviewSet] = useState(0);
  const { ref, isVisible } = useScrollReveal();

  const googleRating = toDisplayRating(ratings?.google.rating);
  const googleReviewCount = ratings?.google.userRatingsTotal ?? null;
  const trustpilotRating = toDisplayRating(ratings?.trustpilot.rating);
  const trustpilotReviewCount = ratings?.trustpilot.reviewCount ?? null;

  const googleFallback = reviewPlatforms.find(
    (platform) => platform.name === "Google",
  );
  const trustpilotFallback = reviewPlatforms.find(
    (platform) => platform.name === "Trustpilot",
  );

  const displayGoogleRating =
    googleRating ?? toDisplayRating(googleFallback?.rating);
  const displayGoogleReviewCount =
    googleReviewCount ?? googleFallback?.reviewCount ?? null;

  const displayTrustpilotRating =
    trustpilotRating ?? toDisplayRating(trustpilotFallback?.rating);
  const displayTrustpilotReviewCount =
    trustpilotReviewCount ?? trustpilotFallback?.reviewCount ?? null;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewSet((prev) => (prev + 1) % reviewSets.length);
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [reviewSets.length, autoScrollInterval]);

  return (
    <section
      ref={ref}
      className={`${sectionSpacingClassName} ${sectionBackgroundClassName} ${sectionTransitionClassName} ${
        isVisible ? sectionVisibleClassName : "opacity-0"
      }`}
      style={
        useVisibilityStyle
          ? {
              visibility: isVisible ? "visible" : "hidden",
            }
          : undefined
      }
    >
      <div className="container mx-auto px-4">
        {showSectionHeading ? (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {title}
            </h2>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto items-stretch">
          {/* Review Badges Card */}
          <Card
            className={`bg-background ${cardBorderClassName} flex flex-col min-h-[320px]`}
          >
            <CardContent className="pt-8 pb-8 px-4 flex flex-col justify-center items-center gap-6 flex-1">
              <div className="flex flex-col items-center gap-2">
                <GcsImage
                  path={googleLogo}
                  alt="Google reviews"
                  className="h-8 w-auto object-contain"
                />
                {displayGoogleRating !== null ? (
                  <>
                    <div className="text-3xl font-bold text-foreground">
                      {displayGoogleRating.toFixed(1)}
                    </div>
                    <RatingStars
                      rating={displayGoogleRating}
                      filledClassName="fill-yellow-400 text-yellow-400"
                    />
                  </>
                ) : (
                  <>
                    <div className="h-10" />
                    <div className="h-5" />
                  </>
                )}
                {displayGoogleReviewCount ? (
                  <a
                    href="https://maps.google.com/?cid=4256186436518853756"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground text-center hover:text-foreground hover:underline transition-colors"
                  >
                    Based on {displayGoogleReviewCount} reviews
                  </a>
                ) : (
                  <div className="h-4" />
                )}
              </div>
              <div className="flex flex-col items-center gap-2">
                <GcsImage
                  path={trustpilotLogo}
                  alt="Trustpilot reviews"
                  className="h-8 w-auto object-contain"
                />
                {displayTrustpilotRating !== null ? (
                  <>
                    <div className="text-3xl font-bold text-foreground">
                      {displayTrustpilotRating.toFixed(1)}
                    </div>
                    <RatingStars
                      rating={displayTrustpilotRating}
                      filledClassName="fill-[#00b67a] text-[#00b67a]"
                    />
                  </>
                ) : (
                  <>
                    <div className="h-10" />
                    <div className="h-5" />
                  </>
                )}
                {displayTrustpilotReviewCount ? (
                  <a
                    href="https://www.trustpilot.com/review/guardianservice.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground text-center hover:text-foreground hover:underline transition-colors"
                  >
                    Based on {displayTrustpilotReviewCount} reviews
                  </a>
                ) : (
                  <div className="h-4" />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Review Cards */}
          {reviewSets[currentReviewSet].map((review, index) => (
            <Card
              key={`${currentReviewSet}-${index}`}
              className={`bg-background ${cardBorderClassName} ${cardAnimationClassName} flex flex-col min-h-[320px] ${
                index === 2 ? "hidden lg:block" : ""
              }`}
            >
              <CardContent
                className={`${cardContentPadding} flex flex-col justify-between flex-1`}
              >
                <div>
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-amber-400 text-amber-400"
                      />
                    ))}
                  </div>
                  <p className="text-foreground mb-6 text-sm leading-relaxed">
                    {review.text}
                  </p>
                </div>
                <div>
                  <div className="font-bold text-primary uppercase text-sm mb-1">
                    {review.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {review.date}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Dot Navigation */}
        <div className="flex justify-center gap-2 mt-8">
          {reviewSets.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentReviewSet(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                currentReviewSet === index
                  ? "bg-foreground"
                  : "bg-foreground/30 hover:bg-foreground/50"
              }`}
              aria-label={`View testimonial set ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
